// askSmart-ui/src/pages/TOSP/portal/PortalMenu.tsx
import React, { useEffect, useRef, useState } from "react";
import { PortalView } from "./TospPortal";

interface PortalMenuProps {
  /** True when this account is the tenancy's primary contact, not a delegate. */
  isPrimary: boolean;
  onNavigate: (view: PortalView) => void;
  /** Widens with the sales sheet so the gear stays over the content, not beside it. */
  wide?: boolean;
}

/**
 * The portal's one menu, top right on every screen.
 *
 * Rendered by the shell rather than by each screen, so it can't drift between
 * them and a tenant never has to go back to the dashboard to reach something.
 */
export default function PortalMenu({ isPrimary, onNavigate, wide }: PortalMenuProps) {
  const [open, setOpen] = useState(false);
  const holder = useRef<HTMLDivElement>(null);

  // A menu that only closes by pressing its own button is a menu people leave
  // open. Escape and a click elsewhere both dismiss it.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (holder.current && !holder.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (view: PortalView) => {
    setOpen(false);
    onNavigate(view);
  };

  return (
    <div className={`tosp-portal-menubar${wide ? " tosp-portal-wide" : ""}`}>
      <div className="tosp-portal-gear" ref={holder}>
        <button
          type="button"
          className="tosp-portal-gear-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Menu"
          title="Menu"
          onClick={() => setOpen((was) => !was)}
        >
          ⚙
        </button>

        {open && (
          <div className="tosp-portal-menu" role="menu">
            <button type="button" role="menuitem" onClick={() => go("dashboard")}>
              Dashboard
            </button>
            <button type="button" role="menuitem" onClick={() => go("sales")}>
              Sales
            </button>
            {/* Between Sales and Expenses, the order the dashboard tiles are
                in — one list of screens, read the same way in both places. */}
            <button type="button" role="menuitem" onClick={() => go("lease")}>
              Lease
            </button>
            <button type="button" role="menuitem" onClick={() => go("expenses")}>
              Expenses
            </button>
            {/* Only the primary contact. A delegate has been lent access to a
                business and can't lend it on, so the option isn't shown rather
                than shown and refused. */}
            {isPrimary && (
              <button type="button" role="menuitem" onClick={() => go("delegates")}>
                Delegates
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
